import React, { useMemo } from 'react';
import { Typography, Grid, Box, Paper, Chip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Udalost } from '../api/JsdhNotifikaceService/generated';
import type { MapConfig, MapaLayout, PoplachTechnikaResponse, PosilovaNotifikaceLogResponse } from '../api/JsdhNotifikaceService/generated';
import type { UdalostRouteResponse } from '../api/JsdhNotifikaceService/generated/models/UdalostRoute';
import JsdhNotifikaceService from '../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import { CasOdVyhlaseni } from './CasOdVyhlaseni';
import MapZoomWrapper from "./MapZoomWrapper";
import { formatAdresa } from '../utils/adresaUdalosti';

interface DetailPoplachusProps {
    aktivniPoplach: Udalost;
    showMapa?: boolean;
    mapConfig?: MapConfig;
    hideTechnikaJednotky?: boolean;
    mapaLayout?: MapaLayout;
    routeData?: UdalostRouteResponse | null;
    stationGps?: string;
    showCasVyhlaseni?: boolean;
    deviceToken?: string;
}

// Parsovat string techniky na části (jednotka, typ vozidla, volací znak)
export const parseTechnika = (technika: string) => {
    const [jednotka, zbytek] = technika.split(':').map(s => s.trim());
    const [typ, volaci] = (zbytek || '').split('-').map(s => s.trim());
    return { jednotka, typ, volaci };
};

const DetailPoplachu: React.FC<DetailPoplachusProps> = ({
    aktivniPoplach,
    showMapa = true,
    mapConfig,
    hideTechnikaJednotky = false,
    mapaLayout = 'misto',
    routeData,
    stationGps,
    showCasVyhlaseni = true,
    deviceToken,
}) => {

    // Query na detaily techniky s info o zdroji (KOPIS/VELITEL)
    const { data: technikaSources } = useQuery<PoplachTechnikaResponse[]>({
        queryKey: ['udalost-technika', aktivniPoplach.id, deviceToken],
        queryFn: () => JsdhNotifikaceService.getUdalostTechnika(aktivniPoplach.id, deviceToken),
        enabled: Boolean(aktivniPoplach.id),
        refetchInterval: 5000,
        refetchOnWindowFocus: false,
        staleTime: 4000,
    });

    // Query na log posilových notifikací
    const { data: notifikaceLog } = useQuery<PosilovaNotifikaceLogResponse[]>({
        queryKey: ['udalost-notifikace-log', aktivniPoplach.id, deviceToken],
        queryFn: () => JsdhNotifikaceService.getNotifikaceLog(aktivniPoplach.id, deviceToken),
        enabled: Boolean(aktivniPoplach.id),
        refetchInterval: 5000,
        refetchOnWindowFocus: false,
        staleTime: 4000,
    });

    // Map volací znak → source info pro badge na parsed technika kartách
    const sourceByVolaciZnak = useMemo(() => {
        const map = new Map<string, PoplachTechnikaResponse>();
        technikaSources?.forEach(t => map.set(t.volaciZnak, t));
        return map;
    }, [technikaSources]);

    // TEST DATA - smazat po otestování
    const testTechnikaJednotky = [
        "Kyjovice: CAS K25 LIAZ - HOP 001",
        "Pustá Polom: CAS 20 T815 Terno - HOP 002",
    ];
    const testDalsiTechnika = [
        "Bílovec: CAS 20 MAN - HOP 011",
        "Studénka: CAS 30 Tatra - HOP 012",
        "Klimkovice: CAS 24 Scania - HOP 013",
        "Ostrava: AZ 30 Iveco - HOP 014",
        "Opava: CAS 32 Tatra - HOP 015",
        "Hlučín: CAS 20 Mercedes - HOP 016",
        "Kravaře: CAS 24 Volvo - HOP 017",
        "Ludgeřovice: CAS 20 DAF - HOP 018",
        "Dolní Benešov: CAS 30 Renault - HOP 019",
    ];

    // VŽDY použít testovací data pro testování
    // const technikaJednotky = testTechnikaJednotky;
    // const dalsiTechnikaJednotek = testDalsiTechnika;
    const technikaJednotky = aktivniPoplach.technikaJednotky || [];
    const dalsiTechnikaJednotek = aktivniPoplach.dalsiTechnikaJednotek || [];
    const adresa = formatAdresa(aktivniPoplach);

    // Effective layout: zpetna kompatibilita s showMapa prop
    const hasRoute = !!routeData?.polyline && routeData.polyline.length > 0;
    let effectiveLayout: MapaLayout = mapaLayout !== 'misto'
        ? mapaLayout
        : (showMapa !== false ? 'misto' : 'off');
    // Fallback: pokud neni trasa, zobraz pouze mapu mista
    if ((effectiveLayout === 'misto-trasa' || effectiveLayout === 'trasa') && !hasRoute) {
        effectiveLayout = 'misto';
    }

    const hasGps = !!aktivniPoplach.gps;

    // Determine grid sizes based on layout
    const showMistoMap = hasGps && (effectiveLayout === 'misto' || effectiveLayout === 'misto-trasa');
    const showTrasaMap = hasGps && hasRoute && (effectiveLayout === 'trasa' || effectiveLayout === 'misto-trasa');

    let infoMd: number;
    let mapMd: number;
    if (effectiveLayout === 'off' || !hasGps) {
        infoMd = 12;
        mapMd = 0;
    } else if (effectiveLayout === 'misto-trasa') {
        infoMd = 4;
        mapMd = 8; // split between 2 maps (4+4)
    } else {
        infoMd = 5;
        mapMd = 7;
    }

    const routeDistanceLabel = routeData
        ? `${routeData.distanceKm?.toFixed(1).replace('.', ',')} km - ${Math.round(routeData.durationMin ?? 0)} min`
        : undefined;

    const InfoPanel = (
        <Grid size={{ xs: 12, md: infoMd }}>
            {aktivniPoplach.udalost && (
                <Box pb={{ xs: 1, md: 1 }}>
                    <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', fontSize: 'var(--dashboard-event-size)', lineHeight: 1.2 }}>
                        {aktivniPoplach.udalost}
                    </Typography>
                </Box>
            )}
            {aktivniPoplach.objekt && (
                <Box sx={{ mb: 'var(--dashboard-gap)' }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem', lineHeight: 1 }}>
                        Objekt
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--dashboard-info-size)', fontWeight: 500, lineHeight: 1.2 }}>
                        {aktivniPoplach.objekt}
                    </Typography>
                </Box>
            )}
            {aktivniPoplach.popis && (
                <Box sx={{ mb: 'var(--dashboard-gap)' }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem', lineHeight: 1 }}>
                        Popis
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--dashboard-info-size)', fontWeight: 500, lineHeight: 1.2 }}>
                        {aktivniPoplach.popis}
                    </Typography>
                </Box>
            )}
            {aktivniPoplach.upresneni && (
                <Box sx={{ mb: 'var(--dashboard-gap)' }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem', lineHeight: 1 }}>
                        Upřesnění
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--dashboard-info-size)', fontWeight: 500, lineHeight: 1.2 }}>
                        {aktivniPoplach.upresneni}
                    </Typography>
                </Box>
            )}
            {adresa && (
                <Box sx={{ mb: 'var(--dashboard-gap)' }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem', lineHeight: 1 }}>
                        Adresa
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--dashboard-info-size)', fontWeight: 500, lineHeight: 1.2 }}>
                        {adresa}
                    </Typography>
                </Box>
            )}

            {/* Route distance info */}
            {routeDistanceLabel && (effectiveLayout === 'trasa' || effectiveLayout === 'misto-trasa') && (
                <Box sx={{ mb: 'var(--dashboard-gap)' }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem', lineHeight: 1 }}>
                        Trasa
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--dashboard-info-size)', fontWeight: 500, lineHeight: 1.2 }}>
                        {routeDistanceLabel}
                    </Typography>
                </Box>
            )}

            {/* Technika */}
            <Box sx={{ mt: 1 }}>
                {!hideTechnikaJednotky && (
                    <>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.secondary", fontSize: '0.85rem', mb: 0.5 }}>
                            Technika
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {technikaJednotky?.length > 0 ? (
                                technikaJednotky.map((t, index) => {
                                    const { jednotka, typ, volaci } = parseTechnika(t);
                                    const source = volaci ? sourceByVolaciZnak.get(volaci) : undefined;
                                    return (
                                        <Paper key={`technika-${index}`} elevation={1} sx={{ p: 0.75, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                                            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1 }}>
                                                {jednotka}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline' }}>
                                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                                                    {typ}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', color: 'primary.main', fontWeight: 600 }}>
                                                    {volaci}
                                                </Typography>
                                                {source && source.zdroj === 'VELITEL' && (
                                                    <Chip
                                                        size="small"
                                                        label={source.potvrzenoKopis ? 'Velitel ✓ KOPIS' : 'Velitel'}
                                                        sx={{
                                                            height: 16,
                                                            fontWeight: 700,
                                                            fontSize: '0.55rem',
                                                            color: '#fff',
                                                            bgcolor: source.potvrzenoKopis ? 'success.main' : 'warning.main',
                                                            '& .MuiChip-label': { px: 0.5 },
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Paper>
                                    );
                                })
                            ) : (
                                <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.disabled", fontSize: '0.8rem' }}>
                                    Žádná technika
                                </Typography>
                            )}
                        </Box>
                    </>
                )}

                {dalsiTechnikaJednotek?.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.secondary", fontSize: '0.85rem', mb: 0.5 }}>
                            Další jednotky
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                            {dalsiTechnikaJednotek.slice(0, 4).map((t, index) => {
                                const { jednotka, typ, volaci } = parseTechnika(t);
                                return (
                                    <Paper key={`dalsi-technika-${index}`} elevation={1} sx={{ p: 0.75, borderLeft: '3px solid', borderColor: 'grey.400' }}>
                                        <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1 }}>
                                            {jednotka}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline' }}>
                                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                                                {typ}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
                                                {volaci}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                );
                            })}
                            {dalsiTechnikaJednotek.length > 4 && (
                                <Typography sx={{ color: "text.secondary", fontSize: '0.8rem', fontStyle: 'italic' }}>
                                    + {dalsiTechnikaJednotek.length - 4} dalších
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Banner - posilová technika */}
                {aktivniPoplach.posilovaTechnikaTexty && aktivniPoplach.posilovaTechnikaTexty.length > 0 && (
                    <Box sx={{ mt: 1, p: 0.75, borderRadius: 1, bgcolor: 'info.main', color: '#fff' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.2 }}>
                            Povolána další technika: {aktivniPoplach.posilovaTechnikaTexty.join(', ')}
                        </Typography>
                    </Box>
                )}

                {/* Poslední odeslaná zpráva */}
                {notifikaceLog && notifikaceLog.length > 0 && (() => {
                    const last = notifikaceLog[0];
                    const cas = new Date(last.createdAt).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
                    return (
                        <Box sx={{ mt: 0.5, p: 0.75, borderRadius: 1, bgcolor: last.kriticka ? 'error.main' : 'warning.main', color: '#fff' }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.2 }}>
                                Poslední zpráva: {last.text || 'Zpráva'} ({cas})
                            </Typography>
                        </Box>
                    );
                })()}

                {/* Poznámka velitele */}
                {technikaSources?.some(t => t.zdroj === 'VELITEL' && t.poznamka) && (
                    <Box sx={{ mt: 0.5, p: 0.75, borderRadius: 1, bgcolor: 'grey.700', color: '#fff' }}>
                        {technikaSources!.filter(t => t.zdroj === 'VELITEL' && t.poznamka).map((t, i) => (
                            <Typography key={i} sx={{ fontSize: '0.7rem', fontWeight: 500, lineHeight: 1.2 }}>
                                Poznámka ({t.nazev} - {t.volaciZnak}): {t.poznamka}
                            </Typography>
                        ))}
                    </Box>
                )}
            </Box>
        </Grid>
    );

    return (
        <Box sx={{ p: 1, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper' }}>
            <Grid container spacing={0}>
                {/* Nadpis + čas */}
                <Grid size={{ xs: 12 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0} mt={0.5} flexWrap="wrap" gap={1}>
                        {aktivniPoplach.pred && (
                            <Typography variant="h1" sx={{ fontWeight: 'bold', fontSize: 'var(--dashboard-title-size)', lineHeight: 1.1 }}>
                                {aktivniPoplach.pred.toLocaleUpperCase()}
                            </Typography>
                        )}
                        {showCasVyhlaseni && aktivniPoplach?.casVyhlaseni && (
                            <CasOdVyhlaseni casVyhlaseni={aktivniPoplach.casVyhlaseni} />
                        )}
                    </Box>
                </Grid>

                {/* Obsah - informace + mapa vedle sebe */}
                <Grid size={{ xs: 12 }}>
                    <Grid container spacing={0} sx={{ alignItems: 'stretch' }}>
                        {/* Info panel */}
                        {InfoPanel}

                        {/* Layout: misto — jedna mapa místa */}
                        {effectiveLayout === 'misto' && showMistoMap && (
                            <Grid size={{ xs: 12, md: 7 }} sx={{ display: 'flex', height: 'var(--dashboard-map-height)', minHeight: 150 }}>
                                <Box sx={{ flex: 1, mt: { xs: 2, md: 0 } }}>
                                    <MapZoomWrapper
                                        gps={aktivniPoplach.gps}
                                        hasCisloPopisne={!!aktivniPoplach.cisloPopisne}
                                        showHyndrant={aktivniPoplach.pred === "POŽÁR"}
                                        height="100%"
                                        mapConfig={mapConfig}
                                    />
                                </Box>
                            </Grid>
                        )}

                        {/* Layout: trasa — jedna mapa fitted na trasu */}
                        {effectiveLayout === 'trasa' && hasGps && (
                            <Grid size={{ xs: 12, md: 7 }} sx={{ display: 'flex', height: 'var(--dashboard-map-height)', minHeight: 150 }}>
                                <Box sx={{ flex: 1, mt: { xs: 2, md: 0 } }}>
                                    <MapZoomWrapper
                                        gps={aktivniPoplach.gps}
                                        hasCisloPopisne={!!aktivniPoplach.cisloPopisne}
                                        showHyndrant={false}
                                        height="100%"
                                        mapConfig={mapConfig}
                                        routeCoordinates={routeData?.polyline}
                                        stationGps={stationGps}
                                        fitToBounds={hasRoute}
                                    />
                                </Box>
                            </Grid>
                        )}

                        {/* Layout: misto-trasa — dve mapy vedle sebe */}
                        {effectiveLayout === 'misto-trasa' && hasGps && (
                            <>
                                <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', height: 'var(--dashboard-map-height)', minHeight: 150 }}>
                                    <Box sx={{ flex: 1, mt: { xs: 2, md: 0 } }}>
                                        <MapZoomWrapper
                                            gps={aktivniPoplach.gps}
                                            hasCisloPopisne={!!aktivniPoplach.cisloPopisne}
                                            showHyndrant={aktivniPoplach.pred === "POŽÁR"}
                                            height="100%"
                                            mapConfig={mapConfig}
                                        />
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', height: 'var(--dashboard-map-height)', minHeight: 150, pl: '2px' }}>
                                    <Box sx={{ flex: 1, mt: { xs: 2, md: 0 } }}>
                                        <MapZoomWrapper
                                            gps={aktivniPoplach.gps}
                                            hasCisloPopisne={!!aktivniPoplach.cisloPopisne}
                                            showHyndrant={false}
                                            height="100%"
                                            routeCoordinates={routeData?.polyline}
                                            stationGps={stationGps}
                                            fitToBounds={hasRoute}
                                        />
                                    </Box>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};

export default React.memo(DetailPoplachu);
